/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable } from '@angular/core';
import * as _ from 'underscore';
import { ToastrService } from 'ngx-toastr';
import { InvalidArgumentException } from '@core/lib/exceptions';
import { ModalService } from '@core/lib/services/modal.service';
import { ModalConfig } from '@core/lib/components/modal/modal-config';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class NotifyService {
  constructor(private toastr: ToastrService, private modalService: ModalService, private translate: TranslateService) {}

  private toToastrAtSeverity(severity, message, title?) {
    if (title) {
      this.toastr[severity](message, title);
    } else {
      this.toastr[severity](message);
    }
  }

  success(message: string, title?: string) {
    this.toToastrAtSeverity('success', message, title);
  }

  info(message: string, title?: string) {
    this.toToastrAtSeverity('info', message, title);
  }

  warning(message: string, title?: string) {
    this.toToastrAtSeverity('warning', message, title);
  }

  error(message: string, title?: string) {
    this.toToastrAtSeverity('error', message, title);
  }

  critical(message: string, title: string, callback?) {
    if (!(arguments.length === 2 || (arguments.length === 3 && typeof callback === 'function'))) {
      throw new InvalidArgumentException(
        'Arguments "message" and "title" are required and optionally' + ' a callback as third argument'
      );
    }

    return new Promise(resolve => {
      const sub = this.modalService
        .openModal(message, {
          headerContent: title,
          noButtonLabel: this.translate.instant('label.OK')
        })
        .afterClosed.subscribe(value => {
          resolve(value);
          sub.unsubscribe();
        });
    });
  }

  decision(message: string, modalInput: ModalConfig, callback?) {
    if (!(arguments.length === 2 || (arguments.length === 3 && typeof callback === 'function'))) {
      throw new InvalidArgumentException(
        'Arguments "message" and "ModalConfig" are required and optionally' + ' a callback as third argument'
      );
    }
    if (
      typeof modalInput !== 'object' &&
      (_.isUndefined(callback) || (!_.isUndefined(callback) && typeof callback !== 'function'))
    ) {
      throw new InvalidArgumentException(
        'Arguments "message" and "ModalConfig" are required and optionally' + ' a callback as third argument'
      );
    }

    return new Promise(resolve => {
      const sub = this.modalService.openModal(message, modalInput).afterClosed.subscribe(value => {
        resolve(value);
        sub.unsubscribe();
      });
    });
  }
}
